import { API_URL } from '../constants';
import { apiFetch } from './client';

/**
 * GET /admin-api/mockup/generation-logs
 *
 * Server-side validation (Joi) verified against the live API:
 *  - at least one of [eventId, tempEventId, customerId, template, createdAt_min, ts_min] is required
 *  - ts_min requires ts_max (and vice versa), same for createdAt_min / createdAt_max
 *  - dates must be ISO 8601
 *  - other allowed filters: status, httpStatus, modelType, serverHost, responseMode
 *  - paging: page, limit, sortDirection (ASC | DESC)
 *
 * Response: { result: IMockupGenerationLog[], totalCount, page, perPage }
 */

export type GenerationLogStatus = 'ok' | 'error';
export type GenerationLogErrorKind =
    | 'silhouette'
    | 'background_removal'
    | 'validation'
    | 'unavailable'
    | 'internal';

export interface IGenerationLogRequestBlob {
    autoRetryEnabled?: boolean;
    balancedMode?: boolean;
    fastMode?: boolean;
    filename?: string;
    glbOnly?: boolean;
    gpuAcceleration?: boolean;
    includePillowJson?: boolean;
    includePreviews?: boolean;
    includeTexture?: boolean;
    modelType?: string;
    neutralViewer?: boolean;
    noBgProvided?: boolean;
    overrides?: Record<string, unknown>;
    overridesApplied?: boolean;
    preview?: boolean;
    previewViews?: unknown;
    sourceProvided?: boolean;
    targetResolution?: number;
    [key: string]: unknown;
}

export interface IGenerationLogBackgroundRemovalBlob {
    averageAlpha: number | null;
    betterResult: string | null;
    blackBordersCropped: boolean | null;
    confidence: number | null;
    contentRetentionRatio: number | null;
    darkArtifactsRatio: number | null;
    device: string | null;
    edgeQuality: number | null;
    fbaApplied: boolean | null;
    fbaMs: number | null;
    finalSizeWh: [number, number] | null;
    gpuPostprocessing: boolean | null;
    inferenceMs: number | null;
    model: string | null;
    orientationFixed: boolean | null;
    originalSizeWh: [number, number] | null;
    overallQuality: number | null;
    postprocessMs: number | null;
    preprocessMs: number | null;
    previewMode: boolean | null;
    retryImprovement: number | null;
    retryPerformed: boolean | null;
    retryReason: string | null;
    semiTransparentRatio: number | null;
    targetResolution: number | null;
    totalMs: number | null;
    weightedTransparency: number | null;
    [key: string]: unknown;
}

export interface IGenerationLogPillowBlob {
    contourPoints?: number;
    contourStages?: Record<string, number | boolean>;
    faces?: number;
    glbBytes?: number;
    gridGranularity?: number;
    heightRange?: [number, number];
    interiorPoints?: number;
    outlinePoints?: number;
    silhouette?: { components: number; ignoredShare: number; mergedShare: number };
    stitching?: { filledTriangles: number; innerLoops: number; loops: number; rimPoints: number };
    tagBuilt?: boolean | null;
    textureSize?: [number, number];
    thicknessBasis?: string;
    thicknessRatio?: number;
    thicknessSize?: number;
    timingsSec?: Record<string, number>;
    triangles?: number;
    trim?: { bbox: number[]; originalSize: number[]; reason: string | null; wasTrimmed: boolean };
    vertices?: number;
    [key: string]: unknown;
}

export interface IGenerationLogTextureSourceBlob {
    applied: boolean;
    maskSize: [number, number] | null;
    reason: string | null;
    scale: number | null;
    sourceSize: [number, number] | null;
}

export interface IGenerationLogErrorBlob {
    httpStatus: number;
    kind: string;
    message: string;
    stage?: string;
}

/** One row as the server returns it (booleans arrive as 0 / 1 / null). */
export interface IMockupGenerationLog {
    // ---- identification (generation log) ----
    id: number;
    eventId: number | string;
    tempEventId: number | string | null;
    ts: string;
    serverHost: string | null;
    serverVersion: string | null;

    // ---- joined mockup event (mockup-stats) ----
    eventStatus: string | null;
    mockupUrl: string | null;
    imageUrl: string | null;
    genImgUrl: string | null;
    orderExist: number | null;
    template: string | null;
    customerId: number | null;
    sessionId: number | null;
    createdAt: string | null;
    eventErrorMessage: string | null;
    errorMessageDetailed: string | null;
    imageType: string | null;
    initMockupDelay: string | null;
    processDuration: string | null;
    actualMockupDuration: string | null;
    saveDuration: string | null;
    skipSave: number | null;

    // ---- result ----
    status: GenerationLogStatus;
    httpStatus: number | null;
    errorKind: GenerationLogErrorKind | string | null;
    errorMessage: string | null;

    // ---- mode ----
    modelType: string | null;
    targetResolution: number | null;
    rbPreview: number | boolean | null;
    fastMode: number | boolean | null;
    balancedMode: number | boolean | null;
    glbOnly: number | boolean | null;
    neutralViewer: number | boolean | null;
    noBgProvided: number | boolean | null;
    sourceProvided: number | boolean | null;
    includePreviews: number | boolean | null;
    previewCount: number | null;
    overridesCount: number | null;
    overridesApplied: number | boolean | null;
    filename: string | null;

    // ---- timings ----
    totalSec: number | null;
    rbSec: number | null;
    rbInferenceMs: number | null;
    rbFbaMs: number | null;
    rbPostprocessMs: number | null;
    p3dMeshSec: number | null;
    p3dGlbSec: number | null;
    p3dPreviewsSec: number | null;

    // ---- input ----
    sourceW: number | null;
    sourceH: number | null;
    sourceMp: number | null;
    orientationFixed: number | boolean | null;
    blackBordersCropped: number | boolean | null;

    // ---- background removal ----
    rbDevice: string | null;
    rbSkipped: number | boolean | null;
    rbFbaApplied: number | boolean | null;
    rbRetryPerformed: number | boolean | null;
    rbBetterResult: string | null;
    rbContentRetention: number | null;
    rbOverallQuality: number | null;
    rbConfidence: number | null;
    rbEdgeQuality: number | null;
    rbAverageAlpha: number | null;

    // ---- texture ----
    texFromSource: number | boolean | null;
    texScale: number | null;

    // ---- 3D ----
    p3dGlbBytes: number | null;
    p3dTriangles: number | null;
    p3dVertices: number | null;
    p3dOutlinePoints: number | null;
    p3dThicknessRatio: number | null;
    p3dThicknessSize: number | null;
    p3dSilComponents: number | null;
    p3dSilIgnoredShare: number | null;
    p3dSilMergedShare: number | null;
    p3dTagBuilt: number | boolean | null;

    // ---- response ----
    responseMode: string | null;
    responseBytes: number | null;

    // ---- blobs ----
    request: IGenerationLogRequestBlob | null;
    backgroundRemoval: IGenerationLogBackgroundRemovalBlob | null;
    pillow: IGenerationLogPillowBlob | null;
    textureSource: IGenerationLogTextureSourceBlob | null;
    errorJson: IGenerationLogErrorBlob | null;
}

export interface IMockupGenerationLogsResponse {
    result: IMockupGenerationLog[];
    totalCount: number;
    page: number;
    perPage: number;
}

export interface IMockupGenerationLogsQueryParams {
    page?: number;
    limit?: number;
    sortDirection?: 'ASC' | 'DESC';
    /** YYYY-MM-DD (converted to ts_min = start of day UTC) */
    tsMin?: string | null;
    /** YYYY-MM-DD (converted to ts_max = end of day UTC) */
    tsMax?: string | null;
    eventId?: string | number | null;
    tempEventId?: string | number | null;
    customerId?: string | number | null;
    template?: string | null;
    status?: GenerationLogStatus | '' | null;
    httpStatus?: number | string | null;
    modelType?: string | null;
    serverHost?: string | null;
    responseMode?: string | null;
    total?: number;
}

export const GENERATION_LOGS_MAX_LIMIT = 500;

const getAuthHeaders = () => {
    return {
        'Content-Type': 'application/json',
    };
};

const handleResponse = async (res: Response) => {
    if (!res.ok) {
        let message = 'Server Error!';
        try {
            const body = await res.json();
            if (body?.message) message = body.message;
        } catch (e) {
            /* ignore */
        }
        throw new Error(message);
    }
    return await res.json();
};

const toStartOfDayIso = (date: string) => `${date}T00:00:00.000Z`;
const toEndOfDayIso = (date: string) => `${date}T23:59:59.999Z`;

export const stringifyMockupGenerationLogsParameters = (
    params: IMockupGenerationLogsQueryParams
): string => {
    const query: string[] = [];
    try {
        if (params.page) query.push(`page=${params.page}`);
        if (params.limit) query.push(`limit=${params.limit}`);
        if (params.sortDirection) query.push(`sortDirection=${params.sortDirection}`);
        if (params.tsMin && params.tsMax) {
            query.push(`ts_min=${encodeURIComponent(toStartOfDayIso(params.tsMin))}`);
            query.push(`ts_max=${encodeURIComponent(toEndOfDayIso(params.tsMax))}`);
        }
        if (params.eventId != null && params.eventId !== '') query.push(`eventId=${encodeURIComponent(String(params.eventId))}`);
        if (params.tempEventId != null && params.tempEventId !== '') query.push(`tempEventId=${encodeURIComponent(String(params.tempEventId))}`);
        if (params.customerId != null && params.customerId !== '') query.push(`customerId=${encodeURIComponent(String(params.customerId))}`);
        if (params.template) query.push(`template=${encodeURIComponent(params.template)}`);
        if (params.status) query.push(`status=${params.status}`);
        if (params.httpStatus != null && params.httpStatus !== '') query.push(`httpStatus=${params.httpStatus}`);
        if (params.modelType) query.push(`modelType=${encodeURIComponent(params.modelType)}`);
        if (params.serverHost) query.push(`serverHost=${encodeURIComponent(params.serverHost)}`);
        if (params.responseMode) query.push(`responseMode=${encodeURIComponent(params.responseMode)}`);
    } catch (e) {
        console.warn('Error building mockup generation logs query params');
    }
    return query.join('&');
};

export const getMockupGenerationLogs = async (
    queryParams: IMockupGenerationLogsQueryParams
): Promise<IMockupGenerationLogsResponse> => {
    const res = await apiFetch(
        `${API_URL}/mockup/generation-logs?${stringifyMockupGenerationLogsParameters(queryParams)}`,
        { method: 'GET', headers: getAuthHeaders() }
    );
    const data = await handleResponse(res);
    return {
        result: Array.isArray(data?.result) ? data.result : [],
        totalCount: Number(data?.totalCount ?? 0),
        page: Number(data?.page ?? queryParams.page ?? 1),
        perPage: Number(data?.perPage ?? queryParams.limit ?? 50),
    };
};

/**
 * Loads every row matching the filters (ignoring page/limit) by walking pages of
 * GENERATION_LOGS_MAX_LIMIT, capped at `maxRows`. Used for client-side analytics
 * because the endpoint has no aggregate variant.
 */
export const getAllMockupGenerationLogs = async (
    queryParams: IMockupGenerationLogsQueryParams,
    maxRows = 3000
): Promise<{ rows: IMockupGenerationLog[]; totalCount: number; truncated: boolean }> => {
    const rows: IMockupGenerationLog[] = [];
    let page = 1;
    let totalCount = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
        const chunk = await getMockupGenerationLogs({
            ...queryParams,
            page,
            limit: GENERATION_LOGS_MAX_LIMIT,
            sortDirection: 'DESC',
        });
        totalCount = chunk.totalCount;
        rows.push(...chunk.result);
        if (chunk.result.length < GENERATION_LOGS_MAX_LIMIT) break;
        if (rows.length >= totalCount) break;
        if (rows.length >= maxRows) break;
        page += 1;
    }
    return { rows, totalCount, truncated: rows.length < totalCount };
};

/** Server returns booleans as 0 / 1 / null. */
export const asBool = (v: unknown): boolean | null => {
    if (v === null || v === undefined) return null;
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v !== 0;
    if (typeof v === 'string') return v === '1' || v.toLowerCase() === 'true';
    return null;
};

/** Returns the single log for an eventId (there is no /generation-logs/:id endpoint). */
export const getMockupGenerationLogByEventId = async (
    eventId: string | number
): Promise<IMockupGenerationLog | null> => {
    const data = await getMockupGenerationLogs({ eventId, limit: 1, page: 1 });
    return data.result[0] ?? null;
};
